import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://pegasusclinicare.com';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_pharma-compliance-7/artifacts/2ycn5ziy_Gemini_Generated_Image_9vta8x9vta8x9vta-Photoroom.png';

export const SEO = ({ page }) => {
  const meta = SEO_DATA[page] || SEO_DATA.home;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}${meta.path}`} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={`${SITE_URL}${meta.path}`} />
      <meta property="og:image" content={LOGO_URL} />
      <meta property="og:site_name" content="Pegasus Clinicare Pvt. Ltd." />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={LOGO_URL} />

      {/* JSON-LD Schema */}
      {meta.schema && (
        <script type="application/ld+json">{JSON.stringify(meta.schema)}</script>
      )}
    </Helmet>
  );
};

const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Pegasus Clinicare Pvt. Ltd.',
  url: SITE_URL,
  logo: LOGO_URL,
  description: 'Expert regulatory consulting, GMP advisory, facility design, and global drug sourcing for pharmaceutical, medical device, cosmetic, and food industries.',
  foundingDate: '2014',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+91-9850271210',
      contactType: 'sales',
      areaServed: ['IN', 'US', 'EU'],
      availableLanguage: ['English', 'Hindi'],
    },
    {
      '@type': 'ContactPoint',
      telephone: '+91-9158487862',
      contactType: 'sales',
      areaServed: ['IN', 'US', 'EU'],
      availableLanguage: ['English', 'Hindi'],
    },
  ],
  sameAs: [],
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'IN',
  },
  knowsAbout: [
    'Regulatory Affairs Consulting',
    'CDSCO Regulatory Consulting',
    'US FDA Regulatory Consulting',
    'Revised Schedule M GMP Advisory',
    'Pharmaceutical Facility Design',
    'Turnkey Project Execution',
    'Global Drug Sourcing',
    'Cosmetic & Food Regulatory Compliance',
  ],
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How quickly do you respond to inquiries?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We aim to respond to all inquiries within 24 business hours. For urgent regulatory matters, please call us directly.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you work with international companies?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Pegasus Clinicare regularly serves foreign companies seeking regulatory approval in India (CDSCO), as well as Indian companies seeking international market approvals (FDA, EU, etc.).',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you offer free initial consultations?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We offer a complimentary 30-minute regulatory consultation for new inquiries.',
      },
    },
    {
      '@type': 'Question',
      name: 'What industries do you serve?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We serve pharmaceutical, medical device, cosmetic, food, nutraceutical, ayurvedic, and CRO/CMO sectors.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you offer retainer-based regulatory consulting?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We offer project-based, retainer-based, and dedicated consulting engagement models.',
      },
    },
  ],
};

const SERVICE_SCHEMA = (name, description) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  provider: { '@type': 'Organization', name: 'Pegasus Clinicare Pvt. Ltd.' },
  name,
  description,
  areaServed: ['IN', 'US', 'EU'],
});

export const SEO_DATA = {
  home: {
    title: 'Pegasus Clinicare | Regulatory Consulting & Global Drug Sourcing | India',
    description: 'Pegasus Clinicare Pvt. Ltd. offers end-to-end regulatory consulting for pharma, medical devices, cosmetics, and food industries. FDA, CDSCO, GMP, facility design, and global drug sourcing. Trusted since 2014.',
    keywords: 'regulatory consulting India, CDSCO consulting, FDA regulatory services, GMP advisory, drug sourcing, pharmaceutical compliance, medical device regulatory',
    path: '/',
    schema: ORG_SCHEMA,
  },
  about: {
    title: 'About Pegasus Clinicare | Regulatory Experts Since 2014 | India',
    description: 'Learn about Pegasus Clinicare Pvt. Ltd. - our story, expert team, mission, and values. A trusted regulatory consulting and global drug sourcing firm since 2014.',
    keywords: 'about Pegasus Clinicare, regulatory consulting firm India, pharmaceutical consulting company, regulatory affairs experts',
    path: '/about',
  },
  services: {
    title: 'Regulatory Consulting & Pharmaceutical Services | Pegasus Clinicare India',
    description: 'Explore Pegasus Clinicare full range of services - CDSCO and FDA regulatory consulting, Revised Schedule M GMP advisory, facility design, turnkey execution, and global drug sourcing.',
    keywords: 'pharmaceutical regulatory services India, CDSCO consulting services, FDA consulting India, GMP advisory, drug sourcing services',
    path: '/services',
  },
  'regulatory-affairs': {
    title: 'Regulatory Affairs Consulting | Pharma, Devices & Cosmetics | Pegasus Clinicare',
    description: 'Expert regulatory affairs consulting for pharmaceuticals, medical devices, cosmetics, and food - across CDSCO, FDA, and international markets.',
    keywords: 'regulatory affairs consulting India, pharmaceutical regulatory strategy, dossier preparation, CTD dossier',
    path: '/services/regulatory-affairs',
    schema: SERVICE_SCHEMA('Regulatory Affairs Consulting', 'Strategic regulatory planning and end-to-end dossier support for multi-product, multi-market launches.'),
  },
  cdsco: {
    title: 'CDSCO Regulatory Consulting | Drug Approvals & Licensing India | Pegasus Clinicare',
    description: 'Expert CDSCO regulatory consulting - new drug applications, import approvals, manufacturing licenses, clinical trial permissions, and medical device registration in India.',
    keywords: 'CDSCO consulting, new drug application India, drug import license India, CDSCO medical device registration',
    path: '/services/cdsco',
    schema: SERVICE_SCHEMA('CDSCO Regulatory Consulting', 'End-to-end CDSCO compliance support for the Indian market.'),
  },
  fda: {
    title: 'US FDA Regulatory Consulting | IND NDA ANDA 510k | Pegasus Clinicare India',
    description: 'US FDA regulatory consulting - IND, NDA, ANDA submissions, 510(k) for medical devices, FDA Pre-Approval Inspections, and US market entry strategy from India.',
    keywords: 'US FDA consulting India, ANDA filing India, NDA submission, 510k medical device, FDA inspection preparation',
    path: '/services/fda',
    schema: SERVICE_SCHEMA('US FDA Regulatory Consulting', 'US market entry support including IND/NDA/ANDA submissions and 510(k).'),
  },
  gmp: {
    title: 'Revised Schedule M GMP Advisory | GMP Compliance India | Pegasus Clinicare',
    description: 'Expert Revised Schedule M GMP advisory services - gap assessments, SOP writing, audit preparation, and full GMP compliance roadmaps for Indian pharmaceutical manufacturers.',
    keywords: 'Revised Schedule M GMP India, revised Schedule M compliance, GMP gap analysis, pharmaceutical GMP consulting',
    path: '/services/gmp',
    schema: SERVICE_SCHEMA('Revised Schedule M GMP Advisory', 'GMP gap analysis, SOP writing, audit readiness aligned to Revised Schedule M.'),
  },
  'facility-design': {
    title: 'Pharmaceutical Facility Design & Project Consultancy | Pegasus Clinicare',
    description: 'GMP-compliant pharmaceutical facility design, cleanroom planning, HVAC engineering, and regulatory-ready layout consultancy.',
    keywords: 'pharma facility design India, GMP facility layout, cleanroom design India, pharmaceutical HVAC',
    path: '/services/facility-design',
    schema: SERVICE_SCHEMA('Facility Design & Project Consultancy', 'GMP-compliant pharmaceutical facility design and infrastructure planning.'),
  },
  turnkey: {
    title: 'Turnkey Pharma Facility Execution | End-to-End Project Management | Pegasus Clinicare',
    description: 'Complete turnkey pharmaceutical facility setup - from design and civil construction to equipment procurement, qualification, validation, and operational handover.',
    keywords: 'turnkey pharma facility India, pharmaceutical facility setup, pharma project management',
    path: '/services/turnkey',
    schema: SERVICE_SCHEMA('Turnkey Project Execution', 'End-to-end project management for pharma facility setup.'),
  },
  'drug-sourcing': {
    title: 'Global Drug Sourcing | Innovator & Reference Listed Drugs | Pegasus Clinicare',
    description: 'Global sourcing of innovator drugs, branded reference drugs, and Reference Listed Drugs (RLDs) for BA/BE studies, clinical research, and formulation development.',
    keywords: 'global drug sourcing India, reference listed drug procurement, RLD sourcing, innovator drug procurement',
    path: '/services/drug-sourcing',
    schema: SERVICE_SCHEMA('Global Drug Sourcing', 'Procurement of innovator and reference drugs from regulated markets globally.'),
  },
  'cosmetic-food': {
    title: 'Cosmetic & Food Regulatory Consulting | CDSCO BIS FSSAI | Pegasus Clinicare',
    description: 'Regulatory consulting for cosmetics, nutraceuticals, and food companies in India - product registration, labeling compliance, FSSAI licensing, and claims substantiation.',
    keywords: 'cosmetic regulatory India, CDSCO cosmetic registration, FSSAI licensing, nutraceutical regulation India',
    path: '/services/cosmetic-food',
    schema: SERVICE_SCHEMA('Cosmetic & Food Regulatory Compliance', 'Specialized regulatory consulting for cosmetic, nutraceutical, and food products.'),
  },
  industries: {
    title: 'Industries We Serve | Pharmaceutical, Medical Device, Cosmetic & Food | Pegasus Clinicare',
    description: 'Pegasus Clinicare serves pharmaceutical, medical device, cosmetic, food, nutraceutical, herbal, and CRO/CMO industries with expert regulatory consulting.',
    keywords: 'pharmaceutical regulatory consulting, medical device India, cosmetic regulatory India, food regulatory India',
    path: '/industries',
  },
  'why-pegasus': {
    title: 'Why Choose Pegasus Clinicare | Regulatory Consulting Experts India',
    description: 'Discover why leading pharma, device, and consumer health companies choose Pegasus Clinicare - decade-long expertise, multidisciplinary team, and proven results.',
    keywords: 'best regulatory consulting India, pharma compliance partner, regulatory consulting firm',
    path: '/why-pegasus',
  },
  clients: {
    title: 'Our Clients | Trusted by India Leading Pharma & Life Sciences Companies | Pegasus Clinicare',
    description: 'Pegasus Clinicare has partnered with 50+ leading pharma, medical device, cosmetic, and food companies including Lupin, 3M, L\'Oreal, ACG, Bilcare, and more.',
    keywords: 'Pegasus Clinicare clients, regulatory consulting clients India, pharma clients India',
    path: '/clients',
  },
  'knowledge-hub': {
    title: 'Regulatory Insights & Knowledge Hub | Pegasus Clinicare Blog',
    description: 'Stay ahead with regulatory knowledge hub - expert articles on CDSCO updates, FDA regulations, Revised Schedule M GMP, facility design, drug sourcing, and industry news.',
    keywords: 'pharmaceutical regulatory news India, CDSCO updates, FDA regulatory updates, GMP compliance articles',
    path: '/knowledge-hub',
  },
  contact: {
    title: 'Contact Pegasus Clinicare | Regulatory Consulting Inquiry | India',
    description: 'Get in touch with Pegasus Clinicare for regulatory consulting, GMP advisory, facility design, drug sourcing, or any compliance inquiry. Our experts respond within 24 hours.',
    keywords: 'contact regulatory consultant India, pharma compliance inquiry, regulatory consulting services India',
    path: '/contact',
    schema: FAQ_SCHEMA,
  },
};

export default SEO;
